import { EvaluationQuestionAutomatedScoringFocusEnum } from "@genesys/cloud-frontend/models";
import { useAppDispatch, useAppSelector } from "app/hooks";
import FormType from "enums/FormType";
import { MarkdownInline } from "features/common/Markdown";
import { RadialButton } from "features/common/RadialButton";
import { ReadOnlyCheckbox, ReadOnlyRadio } from "features/common/ReadOnlyInput";
import { ScoreTag, ScoreTagLevel } from "features/common/ScoreTag";
import { useStrings } from "features/i18n/useStrings";
import { AssistedMCQOption } from "features/qualityForm/formViewer/evaluationAssistance/AssistedMCQOption";
import {
    selectQuestionGroupIdContainingQuestion,
    selectQuestionMaxScore,
} from "features/qualityForm/qualityFormSlice";
import {
    selectAutomatedScoringFocus,
    selectFormType,
    selectQuestion,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import {
    getPreviewScores,
    scorableSliceActions,
    selectQuestionScore,
    sendAnswersDataOnFormDataChange,
} from "features/scorable/scorableSlice";
import {
    GuxBadge,
    GuxFormFieldCheckbox,
    GuxFormFieldRadio,
    GuxIcon,
    GuxTooltip,
    GuxTruncate,
} from "genesys-spark-components-react";
import React, { useContext } from "react";
import { shallowEqual } from "react-redux";
import { PreviewModeContext, UserModeContext } from "../Contexts";
import strings from "./CommonQuestionComponents.strings";
import "./MultipleChoiceQuestionOptions.less";
import { AiScoringMCQOption } from "features/qualityForm/formViewer/aiScoring/AiScoringMCQOption";
import { AiScoringNAOption } from "features/qualityForm/formViewer/aiScoring/AiScoringNAOption";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";

// Feature common slices
import { selectAnswerOption } from "features/qualityForm/qualityFormSlices/index";
import commonStrings from "features/qualityForm/common/questionComponents/CommonQuestionComponents.strings";
import { getScoreValueAriaLabel } from "util/componentUtil";

function StandardNAOption({
    checked,
    disabled,
    onChange,
    label,
}: {
    checked: boolean;
    disabled: boolean;
    onChange: () => void;
    label: string;
}) {
    return (
        <div className="NAOption">
            {disabled ? (
                <ReadOnlyCheckbox checked={checked} label={label} />
            ) : (
                <GuxFormFieldCheckbox>
                    <input
                        slot="input"
                        type="checkbox"
                        name={label}
                        checked={checked}
                        onChange={onChange}
                        className="Clickable"
                    />
                    <label className="Clickable" slot="label">
                        {label}
                    </label>
                </GuxFormFieldCheckbox>
            )}
        </div>
    );
}

function MultipleChoiceQuestionOption({
    answerOptionId,
    disabled,
    selected,
    onSelected,
    questionLevel = "default",
    hideScore = false,
    canReviewResults = false,
    questionMaxScore,
}: {
    answerOptionId: string;
    disabled: boolean;
    selected: boolean;
    questionLevel?: ScoreTagLevel;
    onSelected: (s: string) => void;
    hideScore?: boolean;
    canReviewResults?: boolean;
    questionMaxScore?: number;
}) {
    const t = useStrings(strings);
    const commonT = useStrings(commonStrings);

    const { text, value } = useAppSelector((state) => {
        const { text, value } = selectAnswerOption(state, answerOptionId) ?? {
            text: "",
            type: "",
        };
        return { text, value };
    }, shallowEqual);
    const isSurveyScoringView = useAppSelector((state) => {
        return selectFormType(state) === FormType.SURVEY;
    });

    let resultBadgeColor;
    let resultBadgeIcon;
    let resultLabelMessage;
    if (value != null && questionMaxScore != null) {
        if (value >= questionMaxScore) {
            resultBadgeColor = "success";
            resultBadgeIcon = "alert-success";
            resultLabelMessage = t.labels.correct;
        } else if (value === 0) {
            resultBadgeColor = "error";
            resultBadgeIcon = "close-circle";
            resultLabelMessage = t.labels.incorrect;
        } else {
            resultBadgeColor = "warning";
            resultBadgeIcon = "alert-warning-triangle";
            resultLabelMessage = t.labels.partial;
        }
    }

    return (
        <div
            className={`MultipleChoiceQuestionOption ${
                selected ? "selected" : ""
            }`}
        >
            {disabled ? (
                <>
                    <ReadOnlyRadio checked={selected} label={text} />
                    {!hideScore ? (
                        <ScoreTag
                            mode={questionLevel}
                            selected={selected}
                            ariaLabel={getScoreValueAriaLabel(
                                commonT.labels,
                                questionLevel,
                                value ?? 0
                            )}
                        >
                            {value}
                        </ScoreTag>
                    ) : (
                        canReviewResults &&
                        !!resultBadgeIcon && (
                            <GuxBadge accent={resultBadgeColor}>
                                <GuxIcon
                                    class="result-icon"
                                    iconName={resultBadgeIcon}
                                    decorative
                                ></GuxIcon>
                                <div className="result-label">
                                    {resultLabelMessage}
                                </div>
                            </GuxBadge>
                        )
                    )}
                </>
            ) : (
                <>
                    {isSurveyScoringView ? (
                        text?.trim() === "" ? (
                            <div className="invalid-input-placeholder">
                                <RadialButton
                                    selected={selected}
                                    text={t.labels.defaultAnswerText}
                                    value={answerOptionId}
                                    onChange={({ target }) => {
                                        onSelected(target.defaultValue);
                                    }}
                                    verticalAlign={false}
                                />
                                <GuxIcon
                                    iconName="fa/hexagon-exclamation-solid"
                                    screenreaderText={t.labels.invalidInput}
                                ></GuxIcon>
                            </div>
                        ) : (
                            <RadialButton
                                selected={selected}
                                text={text}
                                value={answerOptionId}
                                onChange={({ target }) => {
                                    onSelected(target.defaultValue);
                                }}
                                verticalAlign={false}
                            />
                        )
                    ) : (
                        <GuxFormFieldRadio>
                            <input
                                slot="input"
                                type="radio"
                                id={answerOptionId}
                                checked={selected}
                                onChange={({ target }) => onSelected(target.id)}
                            />
                            {text?.trim() === "" ? (
                                <label
                                    slot="label"
                                    htmlFor={answerOptionId}
                                    className="invalid-input-placeholder"
                                >
                                    {t.labels.defaultAnswerText}
                                    <GuxIcon
                                        iconName="fa/hexagon-exclamation-solid"
                                        screenreaderText={t.labels.invalidInput}
                                    ></GuxIcon>
                                </label>
                            ) : (
                                <label
                                    slot="label"
                                    htmlFor={answerOptionId}
                                    className="editor-answer-option-label"
                                >
                                    <GuxTruncate
                                        max-lines={15}
                                        data-testid="answer-option-text"
                                    >
                                        <MarkdownInline
                                            markdownInlineText={text ?? ""}
                                        ></MarkdownInline>
                                    </GuxTruncate>
                                </label>
                            )}
                        </GuxFormFieldRadio>
                    )}
                </>
            )}
        </div>
    );
}

export default function MultipleChoiceQuestionOptions({
    questionId,
    disabled = true,
    hideScore = false,
    canReviewResults = false,
}: {
    questionId: string;
    disabled?: boolean;
    hideScore?: boolean;
    canReviewResults?: boolean;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const { previewMode } = useContext(PreviewModeContext);
    const answerOptions =
        useAppSelector(
            (state) => selectQuestion(state, questionId)?.answerOptions
        ) ?? [];
    const { naEnabled, isCritical } = useAppSelector((state) => {
        const { naEnabled, isCritical } = selectQuestion(state, questionId) ?? {
            naEnabled: false,
            isCritical: false,
            isKill: false,
        };
        return { naEnabled, isCritical };
    }, shallowEqual);

    const questionGroupId = useAppSelector(
        (state) =>
            selectQuestionGroupIdContainingQuestion(questionId)(
                state.qualityForm
            ) ?? ""
    );
    const saveNewAnswer = (newSelectedAnswerId: string) => {
        const setAnswer = () => {
            dispatch(
                scorableSliceActions.setAnswer({
                    questionGroupId,
                    questionId,
                    answerId: newSelectedAnswerId,
                })
            );
        };
        dispatch(sendAnswersDataOnFormDataChange(setAnswer));

        if (previewMode) {
            dispatch(getPreviewScores({ isScorableFinished: false }));
        }
    };

    const handleNAChange = () => {
        const changeQuestionNA = () => {
            dispatch(
                scorableSliceActions.changeQuestionNA({
                    questionGroupId,
                    questionId,
                })
            );
        };
        dispatch(sendAnswersDataOnFormDataChange(changeQuestionNA));
        if (previewMode) {
            dispatch(
                getPreviewScores({
                    isScorableFinished: false,
                })
            );
        }
    };

    const { isAdmin } = useContext(UserModeContext);
    const {
        answerId: selectedAnswer,
        markedNA,
        failedKillQuestion: fatal,
        assistedAnswerId,
        questionMaxScore,
        aiAnswer,
    } = useAppSelector((state) => {
        const {
            answerId,
            markedNA,
            failedKillQuestion,
            assistedAnswerId,
            aiAnswer,
        } = selectQuestionScore(state, questionId) ?? {
            answerId: "",
            markedNA: false,
            failedKillQuestion: false,
            assistedAnswerId: "",
            aiAnswer: undefined,
        };
        const questionMaxScore = selectQuestionMaxScore(state, questionId);
        return {
            answerId,
            markedNA,
            failedKillQuestion,
            assistedAnswerId,
            questionMaxScore,
            aiAnswer,
        };
    }, shallowEqual);
    const questionLevel =
        fatal && !markedNA ? "fatal" : isCritical ? "critical" : undefined;

    // Check AI Scoring
    const aiScoringFTEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.AI_SCORING);
    });
    const lensifyAiScoringFTEnabled = useAppSelector((state) => {
        return selectFeatureToggle(state, FeatureToggle.LENSIFY_AISCORING);
    });
    const isWem1511EvaluationFocusEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.WEM_1511_EVALUATION_FOCUS)
    );
    const automatedScoringFocus = useAppSelector((state) =>
        selectAutomatedScoringFocus(state, questionId)
    );
    const focusLabelText: string | null =
        (isWem1511EvaluationFocusEnabled &&
            (automatedScoringFocus ===
            EvaluationQuestionAutomatedScoringFocusEnum.FullInteraction
                ? t.labels.fullInteractionFocus
                : automatedScoringFocus ===
                  EvaluationQuestionAutomatedScoringFocusEnum.EvaluatedAgent
                ? t.labels.evaluatedAgentFocus
                : null)) ||
        null;
    const isEvaluationScoringView = useAppSelector((state) => {
        return selectFormType(state) === FormType.EVALUATION;
    });
    const aiAnswerId = aiAnswer?.answerId;
    const aiAnswerFailure = aiAnswer?.failureType;
    const aiMarkedNotApplicable = aiAnswer?.markedNotApplicable;
    const shouldShowAiScoringNAOption =
        isEvaluationScoringView &&
        aiScoringFTEnabled &&
        lensifyAiScoringFTEnabled &&
        isAdmin &&
        aiMarkedNotApplicable === true &&
        aiAnswerFailure === undefined &&
        !aiAnswerId;

    const multipleChoiceQuestionOptionComponent = (value: string) => {
        if (isEvaluationScoringView) {
            if (isAdmin && assistedAnswerId === value) {
                return (
                    <>
                        <div className="evaluation-assistance-quote">
                            <GuxIcon
                                iconName="quote-left"
                                data-testid="evaluation-assistance-quote"
                                screenreader-text={t.labels.assistedAnswer}
                            />
                            <GuxTooltip>{t.labels.assistedAnswer}</GuxTooltip>
                        </div>
                        <AssistedMCQOption
                            answerOptionId={value}
                            selected={selectedAnswer === value}
                            key={value}
                            onSelected={saveNewAnswer}
                            disabled={disabled}
                            questionLevel={questionLevel}
                            hideScore={hideScore}
                            focusLabelText={focusLabelText}
                        />
                    </>
                );
            } else if (aiScoringFTEnabled) {
                if (
                    isAdmin &&
                    aiAnswerId === value &&
                    aiAnswerFailure === undefined
                ) {
                    const explanation = aiAnswer?.explanation ?? "";
                    return (
                        <>
                            <div
                                className="ai-scoring-icon"
                                data-testid="ai-scoring-icon"
                            >
                                <GuxIcon
                                    iconName="restricted/custom/sparkles-solid"
                                    screenreader-text={
                                        t.labels.aiSuggestedAnswer
                                    }
                                />
                                <GuxTooltip>
                                    {t.labels.aiSuggestedAnswer}
                                </GuxTooltip>
                            </div>
                            <AiScoringMCQOption
                                answerOptionId={value}
                                selected={selectedAnswer === value}
                                key={value}
                                onSelected={saveNewAnswer}
                                disabled={disabled}
                                questionLevel={questionLevel}
                                hideScore={hideScore}
                                explanation={explanation}
                                focusLabelText={focusLabelText}
                            />
                        </>
                    );
                }
            }
        }

        // for Survey, Assessment MCQ options + non Eval assistance / AI answer configured option + FT disabled AI answer option
        return (
            <MultipleChoiceQuestionOption
                answerOptionId={value}
                disabled={disabled}
                selected={selectedAnswer === value}
                key={value}
                onSelected={saveNewAnswer}
                questionLevel={questionLevel}
                hideScore={hideScore}
                canReviewResults={canReviewResults}
                questionMaxScore={questionMaxScore}
            />
        );
    };

    return (
        <div className="MultipleChoiceQuestionOptions">
            {answerOptions.map((value) => {
                return (
                    <React.Fragment key={value}>
                        {multipleChoiceQuestionOptionComponent(value)}
                    </React.Fragment>
                );
            })}

            {naEnabled && (
                <>
                    {shouldShowAiScoringNAOption ? (
                        <AiScoringNAOption
                            checked={markedNA ?? false}
                            disabled={disabled}
                            onChange={handleNAChange}
                            explanation={aiAnswer?.explanation ?? ""}
                            focusLabelText={focusLabelText}
                        />
                    ) : (
                        <StandardNAOption
                            checked={markedNA ?? false}
                            disabled={disabled}
                            onChange={handleNAChange}
                            label={t.labels.na}
                        />
                    )}
                </>
            )}
        </div>
    );
}
